import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ChevronsUpDown, Car, Route as RouteIcon, Loader2 } from "lucide-react";
import { tripService, vehicleService } from "@/services/api";
import { useFetch } from "@/hooks/useFetch";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import { clockTime, shortDate, fmtKm, fmtDuration } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Skeleton } from "@/components/ui/skeleton";
import { LoadMoreSentinel } from "@/components/common/LoadMoreSentinel";
import { cn } from "@/lib/utils";

/**
 * Every trip for one vehicle, with the vehicle chosen from a searchable list.
 *
 * Replaces the start/end record that used to sit here — that moved to the
 * horizontal timeline under the map, where it can be read against the speed
 * profile. This column is now for moving between trips, which is what a
 * sidebar next to a single trip is actually useful for.
 *
 * Defaults to the vehicle of the trip being viewed, so opening a trip and
 * wanting "the one before this" takes no selection at all.
 */

export function VehicleTripsPanel({ initialVehicleId, currentTripId, className }) {
  const navigate = useNavigate();
  const [vehicleId, setVehicleId] = useState(initialVehicleId ?? null);
  const [open, setOpen] = useState(false);
  const scrollRef = useRef(null);

  // Follow the URL: navigating to a trip on a different vehicle should move the
  // picker with it, rather than leaving the list showing the previous vehicle.
  useEffect(() => {
    if (initialVehicleId) setVehicleId(initialVehicleId);
  }, [initialVehicleId]);

  const { data: vehicles } = useFetch(() => vehicleService.list(), []);

  // Page 1 is the live table (~last 20 days); every page after walks
  // further back through trip_archive in 20-day windows — see the matching
  // comment in Trips.jsx for why the archival split makes this safe.
  const fetchPage = useCallback(async (cursor) => {
    if (!vehicleId) return { items: [], nextCursor: undefined, hasMore: false };
    if (cursor === undefined) {
      const trips = await tripService.list({ vehicleId });
      const sorted = [...trips].sort((a, b) => (b.startTime?.getTime() || 0) - (a.startTime?.getTime() || 0));
      return { items: sorted, nextCursor: new Date().toISOString(), hasMore: true };
    }
    const { trips, windowStart, hasMore } = await tripService.archive({ vehicleId, before: cursor, windowDays: 20 });
    return { items: trips, nextCursor: windowStart, hasMore };
  }, [vehicleId]);

  const { items: ordered, loading, loadingMore, hasMore, loadMore } = useInfiniteScroll(fetchPage, [vehicleId]);

  const selected = useMemo(
    () => (vehicles || []).find((v) => v.id === vehicleId) || null,
    [vehicles, vehicleId]
  );

  return (
    <div className={cn("flex flex-col overflow-hidden rounded-2xl border border-border/70 bg-card/60", className)}>
      <div className="border-b border-border/60 p-4">
        <h3 className="mb-2.5 font-display text-sm font-medium">Trips</h3>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="secondary"
              role="combobox"
              aria-expanded={open}
              className="w-full justify-between font-normal"
              data-testid="trip-vehicle-picker"
            >
              <span className="flex min-w-0 items-center gap-2">
                <Car className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="truncate">{selected?.name || "Select a vehicle"}</span>
              </span>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-60" />
            </Button>
          </PopoverTrigger>
          {/* Width matched to the trigger so the list lines up with the button
              instead of floating at its own width. */}
          <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search vehicles…" data-testid="trip-vehicle-search" />
              <CommandList>
                <CommandEmpty>No vehicle matches that.</CommandEmpty>
                <CommandGroup>
                  {(vehicles || []).map((v) => (
                    <CommandItem
                      key={v.id}
                      // cmdk filters on this value, so everything worth
                      // searching by goes in it — a plate or VIN is often what
                      // someone has to hand rather than the nickname.
                      value={[v.name, v.plate, v.vin, v.make, v.model].filter(Boolean).join(" ")}
                      onSelect={() => {
                        setVehicleId(v.id);
                        setOpen(false);
                      }}
                    >
                      <Check className={cn("mr-2 h-4 w-4", v.id === vehicleId ? "opacity-100" : "opacity-0")} />
                      <span className="min-w-0">
                        <span className="block truncate text-sm">{v.name}</span>
                        {v.plate && <span className="block truncate text-xs text-muted-foreground">{v.plate}</span>}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto p-2 no-scrollbar">
        {loading ? (
          <div className="space-y-2 p-2">
            {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-16 w-full rounded-xl" />)}
          </div>
        ) : ordered.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
            <RouteIcon className="h-6 w-6 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {selected ? `No trips recorded for ${selected.name} yet.` : "Pick a vehicle to see its trips."}
            </p>
          </div>
        ) : (
          <ul className="space-y-1.5">
            {ordered.map((t) => {
              const isCurrent = t.id === currentTripId;
              return (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => navigate(`/trips/${t.id}`)}
                    aria-current={isCurrent ? "true" : undefined}
                    className={cn(
                      "w-full rounded-xl border p-2.5 text-left transition",
                      isCurrent
                        ? "border-primary/40 bg-primary/10"
                        : "border-transparent hover:border-border/70 hover:bg-secondary/40"
                    )}
                    data-testid={isCurrent ? "trip-list-current" : "trip-list-item"}
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-sm font-medium">
                        {shortDate(t.startTime, t.utcOffsetMinutes)}
                        <span className="ml-1.5 font-telemetry text-xs text-muted-foreground">
                          {clockTime(t.startTime, t.utcOffsetMinutes)}
                        </span>
                      </span>
                      {t.status === "in-progress" && (
                        <span className="shrink-0 rounded-full border border-emerald-400/25 bg-emerald-400/10 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 dark:text-emerald-300">
                          Live
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 truncate text-xs text-muted-foreground" title={`${t.startLoc} → ${t.endLoc}`}>
                      {t.startLoc} → {t.endLoc}
                    </div>
                    <div className="mt-1 flex gap-3 font-telemetry text-[11px] text-muted-foreground">
                      <span>{fmtKm(t.distanceKm)}</span>
                      <span>{fmtDuration(t.durationMin)}</span>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        {!loading && hasMore && (
          <>
            <LoadMoreSentinel onVisible={loadMore} disabled={loadingMore} root={scrollRef.current} testId="trip-list-load-more" />
            {loadingMore && (
              <div className="flex items-center justify-center gap-2 py-3 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading older trips…
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
